/**
 * abc-behavioral.js
 * localStorage-based behavioral tracking. Consent-gated: reads/writes only when
 * window.__abc.consent.preferences === true.
 *
 * Runs for ALL visitors — anonymous and logged-in alike.
 * Logged-in users with 1+ orders have richer data in window.__abc.customer
 * but still accumulate behavioral signals here.
 *
 * localStorage keys:
 *   abc_v        — { count, lastVisit, firstVisit }             no TTL
 *   abc_products — { ids: [...], expires: epochMs }             90 days
 *   abc_cat      — { handle, expires: epochMs }                 90 days
 *   abc_ref      — { source, expires: null }                    forever
 *   abc_seen     — { <offerKey>: true, expires: epochMs }       30 days
 *
 * Visit counter rules:
 *   - 1 increment per calendar day (multiple tabs/sessions = still 1)
 *   - Decay: if last visit was 60+ days ago, reset count to 1
 *
 * Fires: abc:behavioralReady
 *
 * Public API (window.__abc.behavioral after init):
 *   trackProduct(productId)     — call on PDP page load
 *   trackCategory(handle)       — call on collection page load
 *   markOfferSeen(key)          — call when an offer is displayed
 *   hasSeenOffer(key)           — returns bool
 *   visitCount                  — integer
 *   visitStage                  — 'Discovery'|'Consideration'|'Conversion'|'Subscription'
 *   lastProducts                — array of product ID strings (max 10)
 *   lastCategory                — collection handle string or null
 *   refSource                   — first arrival UTM source or null
 */
(function () {
  'use strict';

  var DECAY_DAYS   = 60;
  var PRODUCT_TTL  = 90 * 24 * 60 * 60 * 1000;
  var CAT_TTL      = 90 * 24 * 60 * 60 * 1000;
  var SEEN_TTL     = 30 * 24 * 60 * 60 * 1000;
  var MAX_PRODUCTS = 10;

  var LS = {
    visits:   'abc_v',
    products: 'abc_products',
    cat:      'abc_cat',
    ref:      'abc_ref',
    seen:     'abc_seen',
  };

  // ── Consent check ──────────────────────────────────────────────────────────
  function allowed() {
    // Gate on analytics consent — covers behavioral data storage (visit counts, product views).
    // preferences maps to essential_services in Ketch (always true) so is not a useful gate.
    return !!(window.__abc && window.__abc.consent && window.__abc.consent.analytics);
  }

  // ── localStorage helpers ───────────────────────────────────────────────────
  function lsGet(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      // Check TTL (expires: null means no expiry)
      if (obj && obj.expires && Date.now() > obj.expires) {
        localStorage.removeItem(key);
        return null;
      }
      return obj;
    } catch (e) {
      return null;
    }
  }

  function lsSet(key, val) {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) {}
  }

  function lsClear() {
    Object.values(LS).forEach(function (k) { localStorage.removeItem(k); });
  }

  // ── Date helpers ───────────────────────────────────────────────────────────
  function todayStr() {
    return new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
  }

  function daysBetween(dateStr) {
    if (!dateStr) return Infinity;
    var ms = Date.now() - new Date(dateStr).getTime();
    return Math.floor(ms / 864e5);
  }

  // ── Visit counter ──────────────────────────────────────────────────────────
  function updateVisits() {
    if (!allowed()) return null;

    var existing = lsGet(LS.visits) || {};
    var today    = todayStr();

    // Decay: if last visit was 60+ days ago, restart
    if (daysBetween(existing.lastVisit) >= DECAY_DAYS) {
      existing = {};
    }

    var count = existing.count || 0;

    // Increment at most once per calendar day
    if (existing.lastVisit !== today) {
      count += 1;
    }

    var updated = {
      count:      count,
      lastVisit:  today,
      firstVisit: existing.firstVisit || today,
    };

    lsSet(LS.visits, updated);
    return updated;
  }

  // ── Visit stage ────────────────────────────────────────────────────────────
  function deriveStage(count) {
    if (!count || count <= 1) return 'Discovery';
    if (count <= 3) return 'Consideration';
    if (count <= 6) return 'Conversion';
    return 'Subscription';
  }

  // ── First arrival channel ──────────────────────────────────────────────────
  function updateRefSource() {
    if (!allowed()) return;
    // Set once, never overwritten
    var existing = lsGet(LS.ref);
    if (existing) return;
    var source = window.__abc && window.__abc.session && window.__abc.session.utmSource;
    if (source) {
      lsSet(LS.ref, { source: source });
    }
  }

  // ── Public tracking functions ──────────────────────────────────────────────
  function trackProduct(productId) {
    if (!allowed() || !productId) return;
    var id  = String(productId);
    var obj = lsGet(LS.products) || { ids: [] };
    var ids = obj.ids.filter(function (x) { return x !== id; }); // remove if already present
    ids.unshift(id);                                              // prepend
    if (ids.length > MAX_PRODUCTS) ids = ids.slice(0, MAX_PRODUCTS);
    lsSet(LS.products, { ids: ids, expires: Date.now() + PRODUCT_TTL });

    // Keep window.__abc.behavioral in sync
    if (window.__abc && window.__abc.behavioral) {
      window.__abc.behavioral.lastProducts = ids;
    }
  }

  function trackCategory(handle) {
    if (!allowed() || !handle) return;
    lsSet(LS.cat, { handle: handle, expires: Date.now() + CAT_TTL });
    if (window.__abc && window.__abc.behavioral) {
      window.__abc.behavioral.lastCategory = handle;
    }
  }

  function markOfferSeen(key) {
    if (!allowed() || !key) return;
    var obj = (lsGet(LS.seen) || {});
    obj[key] = true;
    obj.expires = Date.now() + SEEN_TTL;
    lsSet(LS.seen, obj);
    if (window.__abc && window.__abc.behavioral) {
      window.__abc.behavioral.seenOffers[key] = true;
    }
  }

  function hasSeenOffer(key) {
    if (!key) return false;
    var obj = lsGet(LS.seen);
    return !!(obj && obj[key]);
  }

  // ── On-login cleanup ───────────────────────────────────────────────────────
  // When a customer logs in, Liquid knows their actual program status so
  // offer suppression flags are stale. Keep browsing signals, discard seen flags.
  function handleLogin() {
    var c = window.__abc && window.__abc.customer;
    if (c && c.loggedIn && c.ordersCount >= 1) {
      localStorage.removeItem(LS.seen);
    }
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  function init() {
    if (!allowed()) {
      window.__abc = window.__abc || {};
      window.__abc.behavioral = null;
      return;
    }

    var visits   = updateVisits();
    var products = lsGet(LS.products);
    var cat      = lsGet(LS.cat);
    var ref      = lsGet(LS.ref);
    var seen     = lsGet(LS.seen);

    updateRefSource();

    var count = visits ? visits.count : 0;

    window.__abc = window.__abc || {};
    window.__abc.behavioral = {
      visitCount:   count,
      visitStage:   deriveStage(count),
      firstVisit:   visits ? visits.firstVisit : null,
      lastVisit:    visits ? visits.lastVisit  : null,
      lastProducts: products ? products.ids : [],
      lastCategory: cat ? cat.handle : null,
      refSource:    ref ? ref.source : null,
      seenOffers:   seen ? (function (o) { delete o.expires; return o; })(Object.assign({}, seen)) : {},
      // Tracking functions
      trackProduct:  trackProduct,
      trackCategory: trackCategory,
      markOfferSeen: markOfferSeen,
      hasSeenOffer:  hasSeenOffer,
    };

    handleLogin();

    // Auto-track current page from server-emitted window.__abc.page
    var page = window.__abc.page;
    if (page) {
      if (page.productId)        trackProduct(String(page.productId));
      if (page.collectionHandle) trackCategory(page.collectionHandle);
    }

    document.dispatchEvent(new CustomEvent('abc:behavioralReady', { detail: window.__abc.behavioral }));
  }

  // ── Boot sequence ──────────────────────────────────────────────────────────
  // Run after consent is known. If consent fires before DOMContentLoaded that's fine too.
  function boot() {
    init();

    // If consent changes mid-session (user accepts via banner), re-init
    document.addEventListener('abc:consentUpdated', function () {
      if (allowed() && !window.__abc.behavioral) {
        init();
      } else if (!allowed() && window.__abc.behavioral) {
        // Consent revoked — clear all stored data and the behavioral object
        lsClear();
        window.__abc.behavioral = null;
      }
    });
  }

  // Wait for consent to be read first
  if (window.__abc && window.__abc.consent) {
    boot();
  } else {
    document.addEventListener('abc:consentReady', boot);
    // Also boot without consent (will expose null behavioral) so downstream
    // code doesn't hang waiting for behavioral to exist
    document.addEventListener('DOMContentLoaded', function () {
      if (!window.__abc || !window.__abc.behavioral) {
        window.__abc = window.__abc || {};
        window.__abc.behavioral = window.__abc.behavioral || null;
      }
    });
  }

})();
